import React, { useEffect, useState } from "react";
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { MetricConfig } from "./MetricConfig";

/**
 * Metric toggle buttons (Growth / Usage / Demographics)
 */
const MetricsToggles = ({ metricConfig, tab, setTab, schoolView = false }) => {
  return (
    <ToggleButtonGroup
      value={tab}
      exclusive
      sx={{ width: { xs: "100%", sm: 400 } }}
      onChange={(_, value) => {
        if (value !== null) setTab(value); // prevent clearing selection
      }}
    >
      {Object.values(metricConfig).map(
        ({ tabIndex, name, enabled, hideOnSchoolView }) => {
          if (schoolView && hideOnSchoolView) {
            return;
          }
          if (enabled) {
            return (
              <ToggleButton key={tabIndex} sx={{ flex: 1 }} value={tabIndex}>
                {name}
              </ToggleButton>
            );
          } else {
            // Wrap disabled button in Tooltip
            return (
              <Tooltip
                key={tabIndex}
                title={`${name} metrics are coming soon`}
                arrow
              >
                <span>
                  <ToggleButton sx={{ flex: 1 }} value={tabIndex} disabled>
                    {name}
                  </ToggleButton>
                </span>
              </Tooltip>
            );
          }
        },
      )}
    </ToggleButtonGroup>
  );
};

/**
 * Metric dropdown selector
 */
const MetricsDropDown = ({ items, selectedMetric, setSelectedMetric }) => {
  return (
    <FormControl sx={{ width: { xs: "100%", sm: 280 } }}>
      <InputLabel id="metric-label">Metric</InputLabel>
      <Select
        labelId="metric-label"
        label="Metric"
        value={selectedMetric}
        onChange={(e) => setSelectedMetric(e.target.value)}
      >
        {items.map((metric) => (
          <MenuItem key={metric} value={metric}>
            {metric}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

/**
 * Combined selection bar
 */
export const MetricSelection = ({
  metricOptions,
  fetchMetricOptions,
  fetchMetrics,
  schoolView = false,
  subscriptionCategory = "ALL",
  setSubscriptionCategory = () => {},
}) => {
  const [tab, setTab] = useState(0);
  const [selectedMetric, setSelectedMetric] = useState("");
  const [availableMetrics, setAvailableMetrics] = useState([]);

  // Fetch metrics whenever tab changes
  useEffect(() => {
    fetchMetricOptions(tab);
  }, [tab, fetchMetricOptions]);

  useEffect(() => {
    setAvailableMetrics(metricOptions);
    if (metricOptions.length > 0) {
      setSelectedMetric(metricOptions[0]);
    } else {
      setSelectedMetric("");
    }
  }, [metricOptions]);

  useEffect(() => {
    if (selectedMetric) {
      fetchMetrics(selectedMetric, tab);
    }
  }, [selectedMetric, tab, fetchMetrics]);

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems={{ xs: "stretch", md: "center" }}
      gap={2}
      sx={{ flexWrap: "wrap" }}
    >
      <MetricsToggles
        metricConfig={MetricConfig}
        tab={tab}
        setTab={(value) => {
          setSelectedMetric("");
          setTab(value);
        }}
        schoolView={schoolView}
      />

      <FormControl sx={{ minWidth: 180 }}>
        <InputLabel id="subscription-category-label">Subscription</InputLabel>
        <Select
          labelId="subscription-category-label"
          label="Subscription"
          value={subscriptionCategory}
          onChange={(event) => setSubscriptionCategory(event.target.value)}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="FREEMIUM">Freemium</MenuItem>
          <MenuItem value="APPROVED">Approved</MenuItem>
        </Select>
      </FormControl>
      <MetricsDropDown
        items={availableMetrics}
        selectedMetric={selectedMetric}
        setSelectedMetric={setSelectedMetric}
      />
    </Stack>
  );
};
